import { type KeyboardEvent, type MouseEvent, useCallback, useId, useState } from 'react';

import { AiFillExclamationCircle } from 'react-icons/ai';

import type { TabsProps } from './types';
import { cn } from '../../utils/cn';
import { handleEventBehavior } from '../../utils/EventBehavior';
import { Truncate } from '../Truncate';

/**
 * A styled tabs component for navigation between different views.
 *
 * @param tabs - Array of tab items with id, label, and optional disabled state.
 * @param selectedId - Controlled selected tab ID.
 * @param defaultSelectedId - Default selected tab for uncontrolled mode.
 * @param onSelect - Callback when a tab is selected.
 * @param className - Additional CSS classes for the container.
 * @param listClassName - Additional CSS classes for the tab list.
 *
 * @example
 * <Tabs
 *   tabs={[
 *     { id: 'tab1', label: 'Kundendaten' },
 *     { id: 'tab2', label: 'Bankanfrage' },
 *     { id: 'tab3', label: 'Angebotsverwaltung' },
 *   ]}
 *   defaultSelectedId="tab1"
 * />
 *
 * @example
 * const [selected, setSelected] = useState('tab1');
 * <Tabs
 *   tabs={tabs}
 *   selectedId={selected}
 *   onSelect={setSelected}
 * />
 *
 * @example
 * <Tabs
 *   tabs={[
 *     { id: 'tab1', label: 'Active' },
 *     { id: 'tab2', label: 'Disabled', disabled: true },
 *   ]}
 * />
 */
export const Tabs: React.FC<TabsProps> = ({
    className,
    defaultSelectedId,
    eventPreventDefault,
    eventStopPropagation,
    listClassName,
    onSelect,
    panelIdPrefix,
    selectedId,
    tabs,
}) => {
    const baseId = useId();
    const isControlled = selectedId !== undefined;

    const [internalSelectedId, setInternalSelectedId] = useState<string>(defaultSelectedId ?? tabs[0]?.id ?? '');

    const activeId = isControlled ? selectedId : internalSelectedId;

    const handleSelect = useCallback(
        (id: string) => {
            if (!isControlled) {
                setInternalSelectedId(id);
            }
            onSelect?.(id);
        },
        [isControlled, onSelect],
    );

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
            const enabledTabs = tabs.filter((tab) => !tab.disabled);
            const currentEnabledIndex = enabledTabs.findIndex((tab) => tab.id === tabs[currentIndex].id);

            let newIndex = -1;

            switch (e.key) {
                case 'ArrowLeft':
                    e.preventDefault();
                    newIndex = currentEnabledIndex > 0 ? currentEnabledIndex - 1 : enabledTabs.length - 1;
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    newIndex = currentEnabledIndex < enabledTabs.length - 1 ? currentEnabledIndex + 1 : 0;
                    break;
                case 'Home':
                    e.preventDefault();
                    newIndex = 0;
                    break;
                case 'End':
                    e.preventDefault();
                    newIndex = enabledTabs.length - 1;
                    break;
                default:
                    break;
            }

            if (newIndex !== -1) {
                const newTab = enabledTabs[newIndex];
                handleSelect(newTab.id);
                const button = document.getElementById(`${baseId}-tab-${newTab.id}`);
                button?.focus();
            }
        },
        [tabs, handleSelect, baseId],
    );

    return (
        <div className={cn('w-full', className)}>
            <div
                role="tablist"
                aria-orientation="horizontal"
                className={cn('inline-flex items-center rounded-md bg-surface-light p-1 gap-1', listClassName)}
            >
                {tabs.map((tab, index) => {
                    const isSelected = activeId === tab.id;
                    const tabId = `${baseId}-tab-${tab.id}`;
                    const panelId = panelIdPrefix ? `${panelIdPrefix}-${tab.id}` : `${baseId}-panel-${tab.id}`;

                    return (
                        <button
                            key={tab.id}
                            id={tabId}
                            role="tab"
                            type="button"
                            aria-selected={isSelected}
                            aria-controls={panelId}
                            aria-disabled={tab.disabled}
                            tabIndex={isSelected ? 0 : -1}
                            disabled={tab.disabled}
                            onClick={(e: MouseEvent<HTMLButtonElement>) => {
                                handleEventBehavior(e, { eventPreventDefault, eventStopPropagation });
                                handleSelect(tab.id);
                            }}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                            className={cn(
                                'relative min-w-0 flex-1 overflow-hidden',
                                'rounded px-3 py-1.5',
                                'text-sm font-normal text-center whitespace-nowrap',
                                'transition-colors select-none',
                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-focus focus-visible:ring-offset-1',
                                isSelected
                                    ? ['bg-midnight text-white', 'shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]']
                                    : ['bg-white text-midnight', 'hover:bg-grey-hover'],
                                tab.disabled && 'cursor-not-allowed opacity-50',
                                tab.error && 'outline outline-1 outline-red-500',
                            )}
                        >
                            {/* Invisible content keeps the button height when the visible label is absolutely positioned */}
                            <span className="invisible inline-flex items-center gap-1" aria-hidden="true">
                                {tab.label}
                                {tab.suffix}
                                {tab.error && <AiFillExclamationCircle className="text-sm" />}
                            </span>
                            <span className="absolute inset-y-0 left-3 right-3 flex min-w-0 items-center justify-center gap-1">
                                <Truncate showTooltip className="min-w-0">
                                    {tab.label}
                                </Truncate>
                                {tab.suffix != null && <span className="shrink-0">{tab.suffix}</span>}
                                {tab.error && <AiFillExclamationCircle className="shrink-0 text-sm text-red-500" />}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

Tabs.displayName = 'Tabs';
